import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { api } from "./api";
import { keys } from "./query-keys";
import { discardChangeResponse, publishChangeResponse, siteActivityResponse } from "@sitebrew/contracts/v1";

type Activity = z.infer<typeof siteActivityResponse>;
const CHECK_POLL_LIMIT_MS = 2 * 60_000;

/** Poll each pending change for at most two minutes; merged changes do not keep the query alive. */
export function activityPollInterval(activity: Activity | undefined, pendingSince: Map<string, number>, now: number): number | false {
  const pending = activity?.changes.filter(({ change, publishable }) => !publishable &&
    ["unknown", "open", "publish_failed"].includes(change.state) &&
    (change.checksState === undefined || change.checksState === "pending")) ?? [];
  const pendingIds = new Set(pending.map(({ change }) => change.changeId));
  for (const id of pendingSince.keys()) if (!pendingIds.has(id)) pendingSince.delete(id);
  const waiting = pending.reduce((anyWaiting, { change }) => {
    const since = pendingSince.get(change.changeId) ?? now;
    pendingSince.set(change.changeId, since);
    return anyWaiting || now - since < CHECK_POLL_LIMIT_MS;
  }, false);
  return activity?.jobs.some(({ job }) => !job.terminal) || waiting ? 5_000 : false;
}

export function useSiteActivity(siteId: string) {
  const pendingSince = useRef(new Map<string, number>());
  return useQuery({
    queryKey: keys.siteActivity(siteId),
    queryFn: () => api(`/sites/${siteId}/activity`, siteActivityResponse),
    refetchInterval: (query) => activityPollInterval(query.state.data, pendingSince.current, Date.now()),
  });
}

export function usePublishChange(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ changeId, headCommit }: { changeId: string; headCommit: string }) =>
      api(`/sites/${siteId}/changes/${changeId}/publish`, publishChangeResponse,
        { method: "POST", body: { headCommit } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.siteActivity(siteId) }),
  });
}

export function useDiscardChange(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changeId: string) => api(`/sites/${siteId}/changes/${changeId}`,
      discardChangeResponse, { method: "DELETE" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.siteActivity(siteId) }),
  });
}
